import React, { FunctionComponent } from "react";
import styled from "styled-components";

import Player from "@vimeo/player";
import { colours } from "@/utils";
import { Device } from "@/utils/device";
import { CL } from "@/components/Typography";
import EmployeeTestimonial from "@/types/marketing/EmployeeTestimonial";

interface CareersTestimonialContentProps extends EmployeeTestimonial {
  videoOnRight?: boolean;
}

const CareersTestimonialContent: FunctionComponent<
  CareersTestimonialContentProps
> = (props) => {
  if (!props.employeeName) return null;

  const playerRef = React.useRef(null);
  const playerContainerRefMobile = React.useRef(null);
  const playerContainerRefDesktop = React.useRef(null);

  React.useEffect(() => {
    destroyPlayer();
    createPlayer();
  }, []);

  function destroyPlayer() {
    if (!playerRef.current) return;
    playerRef.current.destroy();
  }

  function createPlayer() {
    if (!playerContainerRefDesktop.current || !playerContainerRefMobile.current)
      return;

    playerRef.current = new Player(playerContainerRefDesktop.current, {
      url: props.vimeoUrl,
      playsinline: true,
      transparent: false,
    });

    playerRef.current = new Player(playerContainerRefMobile.current, {
      url: props.vimeoUrl,
      playsinline: true,
      transparent: false,
    });
  }
  return (
    <S.Section
      videoOnRight={props.videoOnRight}
      data-cy={`testimonial-${props.employeeName}`}
    >
      <S.VideoContainer
        ref={playerContainerRefDesktop}
        title={props.employeeName}
        desktop
      />
      <S.CopyContainer>
        <S.Title>
          {props.employeeName}
          <br />
          <S.RoxboroughFontWrap>{props.employeeJobTitle}</S.RoxboroughFontWrap>
        </S.Title>
        <S.VideoContainer
          data-cy={`testimonial-video-${props.employeeName}`}
          ref={playerContainerRefMobile}
          title={props.employeeName}
          mobile
        />
        <CL weight="700" data-cy="quote-bold">
          “{props.quoteBold}
        </CL>
        <CL>{props.quoteRegular}”</CL>
      </S.CopyContainer>
    </S.Section>
  );
};

type StyleProps = {
  desktop?: boolean;
  mobile?: boolean;
  videoOnRight?: boolean;
};

const S = () => {};

S.Section = styled.section<StyleProps>`
  width: 100%;
  background: ${colours.shell};
  background-size: cover;
  padding: 10% 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 2rem;

  @media (min-width: ${Device.mobile}) {
    padding: 5% 0%;
  }

  @media (min-width: ${Device.tablet}) {
    padding: 5% 10%;
    gap: 6rem;
    flex-direction: ${(p) => (p.videoOnRight ? "row-reverse" : "row")};
  }
`;

S.VideoContainer = styled("div").withConfig({
  shouldForwardProp: (prop) => prop !== "mobile" && prop !== "desktop",
})<StyleProps>`
  position: relative;
  flex: none;
  box-shadow: 5px 5px 5px -2px ${colours.charcoalFaded};
  border-radius: 0.5rem;
  width: 100%;
  overflow: hidden;
  aspect-ratio: 16/9;

  @supports not (aspect-ratio: 16/9) {
    &::before {
      float: left;
      padding-top: 56%;
      content: "";
    }
    &::after {
      display: block;
      content: "";
      clear: both;
    }
  }

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    width: 100%;
    height: 100%;
  }

  ${(p) => p.desktop && "display: none;"}

  @media (min-width: ${Device.desktop}) {
    width: 55%;

    ${(p) => p.desktop && "display: block;"}
    ${(p) => p.mobile && "display: none;"}
  }
`;

S.CopyContainer = styled.div`
  position: relative;
  width: 100%;
  padding: 0 10%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @media (min-width: ${Device.tablet}) {
    padding: 0;
    gap: 3rem;
  }

  @media (min-width: ${Device.desktop}) {
    padding: 0;
    gap: 4rem;
  }
`;

S.Title = styled.h3`
  font-size: 2rem;
  line-height: 2rem;

  @media (min-width: ${Device.mobile}) {
    font-size: 3rem;
    line-height: 3.5rem;
  }

  @media (min-width: ${Device.tablet}) {
    font-size: 3rem;
  }

  @media (min-width: ${Device.desktop}) {
    font-size: 4rem;
    line-height: 4.5rem;
  }
`;

S.RoxboroughFontWrap = styled.span`
  font-family:
    Roxborough CF,
    sans-serif;
  font-weight: 100;
`;

S.Bold = styled.span`
  font-weight: 700;
`;

S.Italic = styled.span`
  font-style: italic;
`;

S.RoxboroughFontWrap = styled.span`
  font-family:
    Roxborough CF,
    sans-serif;
  font-weight: 100;
`;

S.FormWidthContainer = styled.div`
  width: 100%;
  margin: 2rem 0 0 0;
  padding: 0 2%;

  @media (min-width: ${Device.mobile}) {
    padding: 0 10%;
  }

  @media (min-width: ${Device.desktop}) {
    width: 45%;
    padding: 0;
    margin: 0;
  }
`;

export default CareersTestimonialContent;
